import React, { useState, useEffect } from 'react';
import { List_gigs, Admin_GetGigFullDetails } from '../../Utils/Api';
import { toast } from 'react-toastify';

const Gigs = () => {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'details'
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Gig Details State
  const [selectedGig, setSelectedGig] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchGigs();
  }, []);

  // Fetch all gigs
  const fetchGigs = async () => {
    setLoading(true);
    try {
      const response = await List_gigs();
      if (response && response.data) {
        const sortedGigs = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setGigs(sortedGigs);
      }
    } catch (error) {
      console.error('Error fetching gigs:', error);
      toast.error('Failed to fetch gigs');
    } finally {
      setLoading(false);
    }
  };

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGigs = gigs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(gigs.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Open Gig Details View
  const handleViewInfo = async (gigId) => {
    setDetailsLoading(true);
    setViewMode('details');
    try {
      const response = await Admin_GetGigFullDetails(gigId);
      if (response && response.data?.success && response.data?.data) {
        setSelectedGig(response.data.data);
      } else {
        toast.error("Failed to load gig details");
        setViewMode('list');
      }
    } catch (error) {
      console.error("Error fetching gig full details:", error);
      toast.error("Failed to fetch gig details");
      setViewMode('list');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Render List View
  const renderListView = () => (
    <div className="card custom-card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="card-title mb-0">Gigs List</h6>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table text-nowrap">
            <thead className="table-primary">
              <tr>
                <th scope="col">#</th>
                <th scope="col">Title</th>
                <th scope="col">Industry</th>
                <th scope="col">Budget</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col">Created Date</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && gigs.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No gigs found
                  </td>
                </tr>
              )}

              {!loading && currentGigs.map((gig, index) => (
                <tr key={gig._id || gig.id}>
                  <th scope="row">{indexOfFirstItem + index + 1}</th>
                  <td>{gig.title}</td>
                  <td>{gig.industry?.title ? gig.industry.title : (typeof gig.industry === 'string' ? gig.industry : 'N/A')}</td>
                  <td>${gig.budget}</td>
                  <td>
                    <span className="badge bg-primary-transparent">{gig.type}</span>
                  </td>
                  <td>
                    {gig.status === 0 ? (
                      <span className="badge bg-success-transparent">Active</span>
                    ) : gig.status === 1 ? (
                      <span className="badge bg-warning-transparent">Assigned</span>
                    ) : gig.status === 2 ? (
                      <span className="badge bg-danger-transparent">Completed</span>
                    ) : (
                      <span className="badge bg-secondary-transparent">Unknown</span>
                    )}
                  </td>
                  <td>{new Date(gig.createdAt || Date.now()).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={() => handleViewInfo(gig._id || gig.id)}
                      className="btn btn-icon btn-sm btn-info-transparent rounded-pill"
                      title="View Info"
                    >
                      <i className="ri-information-line"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="d-flex justify-content-end mt-4">
            <nav aria-label="Page navigation">
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );

  // Render Details View
  const renderDetailsView = () => {
    if (detailsLoading) {
      return (
        <div className="card custom-card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading gig details...</p>
          </div>
        </div>
      );
    }

    if (!selectedGig) {
      return (
        <div className="card custom-card">
          <div className="card-body text-center py-5">
            <p className="text-muted">No gig details found.</p>
            <button className="btn btn-primary" onClick={() => setViewMode('list')}>
              <i className="ri-arrow-left-line me-2"></i> Back to Gigs List
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="card custom-card">
        <div className="card-header d-flex justify-content-between align-items-center pb-0 border-bottom-0">
          <h5 className="card-title mb-0">Gig Information</h5>
          <button 
            className="btn btn-outline-primary" 
            onClick={() => setViewMode('list')}
          >
            <i className="ri-arrow-left-line me-2"></i> Back to Gigs List
          </button>
        </div>
        
        <div className="card-body">
          <div className="row mt-3">
            {/* Gig Details Section */}
            <div className="col-12 mb-4">
              <div className="card border border-primary shadow-none mb-0">
                <div className="card-header bg-primary-transparent">
                  <h6 className="card-title mb-0 text-primary">Gig Details</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <span className="text-muted fs-13 d-block mb-1">Title</span>
                      <span className="fw-semibold fs-15">{selectedGig.gig?.title}</span>
                    </div>
                    <div className="col-md-6 mb-3">
                      <span className="text-muted fs-13 d-block mb-1">Gigster</span>
                      <span className="fw-semibold fs-15">
                        {selectedGig.gig?.userId?.name || 'N/A'} {selectedGig.gig?.userId?.email ? `(${selectedGig.gig?.userId?.email})` : ''}
                      </span>
                    </div>
                    <div className="col-md-4 mb-3">
                      <span className="text-muted fs-13 d-block mb-1">Budget</span>
                      <span className="fw-semibold fs-15">${selectedGig.gig?.budget} <span className="badge bg-light text-dark ms-1">{selectedGig.gig?.budget_type}</span></span>
                    </div>
                    <div className="col-md-4 mb-3">
                      <span className="text-muted fs-13 d-block mb-1">Type</span>
                      <span className="fw-semibold fs-15">{selectedGig.gig?.type}</span>
                    </div>
                    <div className="col-md-4 mb-3">
                      <span className="text-muted fs-13 d-block mb-1">Status</span>
                      <span className="fw-semibold fs-15">
                        {selectedGig.gig?.status === 0 ? (
                            <span className="badge bg-success-transparent">Active</span>
                          ) : selectedGig.gig?.status === 1 ? (
                            <span className="badge bg-warning-transparent">Assigned</span>
                          ) : selectedGig.gig?.status === 2 ? (
                            <span className="badge bg-danger-transparent">Completed</span>
                          ) : (
                            <span className="badge bg-secondary-transparent">Unknown</span>
                          )}
                      </span>
                    </div>
                    <div className="col-12 mt-2">
                      <span className="text-muted fs-13 d-block mb-1">Description</span>
                      <p className="fs-14 mb-0 text-dark">{selectedGig.gig?.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Proposals Section */}
            <div className="col-md-6 mb-4">
              <div className="card border border-info shadow-none h-100 mb-0">
                <div className="card-header bg-info-transparent">
                  <h6 className="card-title mb-0 text-info">Proposals ({selectedGig.proposals?.length || 0})</h6>
                </div>
                <div className="card-body p-0">
                  {selectedGig.proposals && selectedGig.proposals.length > 0 ? (
                    <ul className="list-group list-group-flush">
                      {selectedGig.proposals.map(prop => (
                        <li key={prop._id} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">{prop.userId?.name || 'Unknown'}</h6>
                              <small className="text-muted d-block">Proposed Budget: <strong>${prop.budget}</strong></small>
                              <small className="text-muted d-block mt-1">Ref ID: <span className="badge bg-light text-dark font-monospace">#{prop._id?.toString().slice(-6).toUpperCase()}</span></small>
                            </div>
                            <span className={`badge ${prop.status === 1 ? 'bg-success-transparent' : prop.status === 2 ? 'bg-danger-transparent' : 'bg-primary-transparent'}`}>
                              {prop.status === 1 ? 'Accepted' : prop.status === 2 ? 'Rejected' : 'Pending'}
                            </span>
                          </div>
                          {prop.remark && <p className="mb-0 mt-2 fs-13 text-muted border-start border-2 border-info ps-2">{prop.remark}</p>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-5 text-center text-muted d-flex flex-column align-items-center justify-content-center h-100">
                      <i className="ri-inbox-archive-line fs-1 mb-2 text-info opacity-50"></i>
                      <span>No proposals found for this gig.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contract Details Section */}
            <div className="col-md-6 mb-4">
              <div className="card border border-success shadow-none h-100 mb-0">
                <div className="card-header bg-success-transparent">
                  <h6 className="card-title mb-0 text-success">Contract & Working Skillster</h6>
                </div>
                <div className="card-body">
                  {selectedGig.contract ? (
                    <div>
                      <div className="d-flex align-items-center mb-4 bg-light p-3 rounded">
                        <div className="avatar avatar-md rounded-circle me-3">
                          <img src={selectedGig.contract.skillsterId?.profilePicture || "New/assets/images/faces/9.jpg"} alt="avatar" />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-semibold">{selectedGig.contract.skillsterId?.name || "Skillster"}</h6>
                          <span className="text-muted fs-13">{selectedGig.contract.skillsterId?.email || "No Email"}</span>
                        </div>
                      </div>
                      
                      <div className="row gy-3">
                        <div className="col-6">
                          <span className="text-muted fs-13 d-block mb-1">Agreed Budget</span>
                          <span className="fw-semibold fs-15">${selectedGig.contract.budget}</span>
                        </div>
                        <div className="col-6">
                          <span className="text-muted fs-13 d-block mb-1">Contract Status</span>
                          <span className={`badge ${selectedGig.contract.status === 0 ? 'bg-primary-transparent' : selectedGig.contract.status === 1 ? 'bg-success-transparent' : 'bg-warning-transparent'}`}>
                            {selectedGig.contract.status === 0 ? 'Working' : selectedGig.contract.status === 1 ? 'Completed' : 'Other (' + selectedGig.contract.status + ')'}
                          </span>
                        </div>
                        <div className="col-12">
                          <span className="text-muted fs-13 d-block mb-1">Start Date</span>
                          <span className="fw-semibold fs-14">{new Date(selectedGig.contract.dateTime).toLocaleString()}</span>
                        </div>
                        <div className="col-12 mt-3 pt-3 border-top">
                          <span className="text-muted fs-13 d-block mb-2">Final Approvals</span>
                          <div className="d-flex gap-4">
                            <div className="d-flex align-items-center">
                              <span className="fw-semibold me-2">Gigster:</span>
                              {selectedGig.contract.gigster_approval ? 
                                <i className="ri-checkbox-circle-fill text-success fs-18"></i> : 
                                <i className="ri-close-circle-fill text-danger fs-18"></i>
                              }
                            </div>
                            <div className="d-flex align-items-center">
                              <span className="fw-semibold me-2">Skillster:</span>
                              {selectedGig.contract.skillster_approval ? 
                                <i className="ri-checkbox-circle-fill text-success fs-18"></i> : 
                                <i className="ri-close-circle-fill text-danger fs-18"></i>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-muted d-flex flex-column align-items-center justify-content-center h-100 py-4">
                       <i className="ri-file-close-line fs-1 mb-2 text-success opacity-50"></i>
                      <span>No active contract or assigned skillster yet.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="main-content ">
        <div className="row my-4 mx-1">
          <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
            <h1 className="page-title fw-semibold fs-18 mb-0 mt-2">
              {viewMode === 'list' ? 'Gigs Management' : 'Gig Details'}
            </h1>
            <div className="ms-md-1 ms-0 ">
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><a href="#">Management</a></li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {viewMode === 'list' ? 'Gigs' : 'Gig Details'}
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {viewMode === 'list' ? renderListView() : renderDetailsView()}
          
        </div>
      </div>
    </>
  );
};

export default Gigs;
